import { useId } from "react";
import type { ElevateLeadershipSlide } from "@/data/elevateLeadership";
import type { ElevateLeadershipVisual as VisualSpec } from "@/data/elevateLeadershipVisuals";

type Props = { slide: ElevateLeadershipSlide; visual: VisualSpec };

const Flow = ({ slide }: Pick<Props, "slide">) => (
  <ol className="elevate-flow">
    {slide.sections?.map((item, index) => (
      <li key={item.title}>
        <span className="elevate-flow-number" aria-hidden="true">{item.label ?? (slide.id.includes("adkar") ? item.title[0] : /^\d+/.exec(item.title)?.[0] ?? index + 1)}</span>
        <div><h2>{item.title.replace(/^\d+\s*·\s*/, "")}</h2><p>{item.body}</p></div>
      </li>
    ))}
  </ol>
);

const Rules = () => (
  <div className="elevate-rules">
    <div><h2>Written rules</h2><p className="elevate-equation"><span>Input + rules</span><b aria-hidden="true">→</b><strong>Output</strong></p><p>Example: flag any shipment with a missing owner.</p></div>
    <div><h2>Learned patterns</h2><p className="elevate-equation"><span>Examples</span><b aria-hidden="true">→</b><strong>Trained model</strong></p><p className="elevate-equation"><span>New input + model</span><b aria-hidden="true">→</b><strong>Prediction</strong></p><p>Test how well the prediction fits your task.</p></div>
  </div>
);

const Learning = () => (
  <div className="elevate-learning">
    <div><span className="elevate-diagram-label">Training</span><p className="elevate-equation"><span>Training examples</span><b aria-hidden="true">→</b><strong>Trained model</strong></p><p>Training fits patterns. You still need to check each answer.</p></div>
    <div><span className="elevate-diagram-label">Inference</span><p className="elevate-equation"><span>Model + current context</span><b aria-hidden="true">→</b><strong>Draft output</strong></p><p>The context window has a limit. Check the facts and constraints in the answer.</p></div>
    <p className="elevate-diagram-footnote">Adding a fact to a conversation doesn't by itself retrain the model.</p>
  </div>
);

const Brief = ({ slide }: Pick<Props, "slide">) => (
  <div className="elevate-brief">
    <p className="elevate-diagram-label">Fictional source notes</p>
    <div className="elevate-source-notes"><span>Note 1: Tuesday</span><span>Note 2: Wednesday</span><span>Owner: missing</span></div>
    {slide.sections?.map((item, i) => <article key={item.title} data-supported={i === 1}><h2>{item.title}</h2><p>{item.body}</p><span>{i === 0 ? "Stop: invented owner and unsupported certainty" : "Review: resolve both gaps before sending"}</span></article>)}
  </div>
);

const Boundaries = ({ slide }: Pick<Props, "slide">) => <div className="elevate-boundaries">
  {slide.sections?.slice(0, 3).map(item => <article key={item.title}><h2>{item.title}</h2><p>{item.body}</p></article>)}
  <p><strong>Accountable owner</strong><br />{slide.sections?.[3].body}</p>
</div>;

const Relationships = () => <div className="elevate-relationships">
  <div className="elevate-relationships-center"><span className="elevate-diagram-label">Shared workflow</span><h2>Shipment-exception brief</h2></div>
  <div className="elevate-relationships-links" aria-hidden="true">{[0, 1, 2].map(index => <span key={index}><svg width="24" height="34" viewBox="0 0 24 34" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 3v28M6 9l6-6 6 6M6 25l6 6 6-6" /></svg></span>)}</div>
  <div className="elevate-relationships-spokes">
    <article><h2>Coordinator & reviewer</h2><p>Explain the real work.<br />Show where effort and confidence change.</p></article>
    <article><h2>Data owner & security lead</h2><p>Confirm approved inputs.<br />Agree on allowed actions.</p></article>
    <article><h2>Manager & sponsor</h2><p>Protect time and support.<br />Make the review decision.</p></article>
  </div>
  <p><strong>Two-way relationships:</strong> listen to concerns, revise the plan, and explain what changed.</p>
</div>;

const CapabilityGap = () => <div className="elevate-capability-gap">
  <div className="elevate-capability-potential">
    <h2>Technical potential</h2><p>What the technology could help with</p>
    <div className="elevate-capability-use"><h2>Observed use</h2><p>What people use in their work</p></div>
    <p className="elevate-capability-space">The gap to understand</p>
  </div>
  <div className="elevate-gap-bridge"><h2>What helps close the gap?</h2><p>Workflow fit · Approved data · Skills & capacity</p></div>
</div>;

const AgentRisks = () => <div className="elevate-agent-risks">
  <div className="elevate-agent-route">
    <article><svg viewBox="0 0 64 70" aria-hidden="true"><path d="M12 3h27l13 13v50H12zM39 3v14h13M22 31h20M22 42h20M22 53h13" /></svg><h2>Document</h2><p>External content</p></article>
    <span className="elevate-agent-connector" aria-hidden="true">→</span>
    <article><svg viewBox="0 0 64 70" aria-hidden="true"><rect x="9" y="19" width="46" height="39" rx="8" /><path d="M32 8v11M25 8h14M20 44h24" /><circle cx="23" cy="32" r="2" /><circle cx="41" cy="32" r="2" /></svg><h2>Agent</h2><p>Interprets & proposes</p></article>
    <span className="elevate-agent-connector" aria-hidden="true">→</span>
    <article><svg viewBox="0 0 64 70" aria-hidden="true"><path d="M32 4L55 14v20c0 15-23 29-23 29S9 49 9 34V14zM22 31l7 7 14-16" /></svg><h2>Tools & data</h2><p>Actions & access</p></article>
  </div>
  <div className="elevate-agent-controls">
    <article><span>Content boundary</span><h2>Treat as information</h2><p>A source can't grant the agent new permissions.</p></article>
    <article><span>Action boundary</span><h2>Enforce permissions</h2><p>A person approves actions with real consequences.</p></article>
    <article><span>Data boundary</span><h2>Limit access</h2><p>Allow only approved records and capabilities.</p></article>
  </div>
  <p className="elevate-agent-example"><strong>Briefing pilot:</strong> read approved notes and draft a brief. Sending messages and changing records remain outside its permissions.</p>
</div>;

const GovernanceDomains = ({ slide }: Pick<Props, "slide">) => <div className="elevate-governance-domains">
  <svg viewBox="0 0 720 520" preserveAspectRatio="none" aria-hidden="true">
    <ellipse cx="248" cy="184" rx="220" ry="145" /><ellipse cx="472" cy="184" rx="220" ry="145" />
    <ellipse cx="248" cy="336" rx="220" ry="145" /><ellipse cx="472" cy="336" rx="220" ry="145" />
  </svg>
  {slide.sections?.map(item => <article key={item.title}><h2>{item.title}</h2><p>{item.body}</p></article>)}
  <strong className="elevate-governance-shared">Shared <br />accountability</strong>
</div>;

const FourGaps = ({ slide }: Pick<Props, "slide">) => <ol className="elevate-four-gaps">
  {slide.sections?.map((item, index) => <li key={item.title}>
    <span className="elevate-gap-number" aria-hidden="true">0{index + 1}</span>
    <div><h2>{item.title}</h2><p>{item.body}</p></div>
    <svg viewBox="0 0 36 44" aria-hidden="true"><path d="M4 40V4h28v36M4 8l28 14M4 22l28 14" /></svg>
  </li>)}
</ol>;

const BigRocks = ({ slide }: Pick<Props, "slide">) => {
  const prefix = `rocks-${useId().replace(/:/g, "")}`;
  return <div className="elevate-big-rocks">
    <svg viewBox="0 0 800 440" role="img" aria-label="Small fixes first leave major constraints outside the jar. Major constraints first leave space for smaller improvements.">
      <title>Order matters: make room for the major constraint</title>
      <defs>
        <pattern id={`${prefix}-sand`} width="14" height="14" patternUnits="userSpaceOnUse"><rect width="14" height="14" fill="#f4dbba" /><circle cx="4" cy="5" r="1.4" fill="#ba610b" /><circle cx="11" cy="11" r="1" fill="#ba610b" /></pattern>
        {[80, 480].map(x => <clipPath id={`${prefix}-${x}`} key={x}><path d={`M${x} 112h240v240q0 25-25 25h-190q-25 0-25-25z`} /></clipPath>)}
      </defs>
      <text x="200" y="30" textAnchor="middle" className="elevate-rocks-heading">Small fixes first</text>
      <text x="600" y="30" textAnchor="middle" className="elevate-rocks-heading">Major constraint first</text>
      <g clipPath={`url(#${prefix}-80)`}>
        <rect x="80" y="214" width="240" height="170" fill={`url(#${prefix}-sand)`} />
        <path d="M110 213l-12-47 37-29 54 14 20 44-35 22z" className="elevate-rock" />
      </g>
      <path d="M231 90l-14-34 38-15 45 11 25 34-30 18z" className="elevate-rock elevate-rock-outside" />
      <path d="M325 185l-9-34 32-19 40 18 6 36-33 14z" className="elevate-rock elevate-rock-outside" />
      <path d="M340 301l12-41 40-11 35 31-12 42-45 7z" className="elevate-rock elevate-rock-outside" />
      <g clipPath={`url(#${prefix}-480)`}>
        <rect x="480" y="125" width="240" height="260" fill={`url(#${prefix}-sand)`} />
        <path d="M497 331l10-62 50-21 39 31-8 65-48 17zM601 352l-1-58 42-38 56 21 11 56-53 30zM529 239l-27-41 22-55 58-12 28 47-22 54zM624 244l-12-49 27-45 53 17 15 47-37 35z" className="elevate-rock" />
      </g>
      {[80, 480].map(x => <g key={x} className="elevate-jar"><path d={`M${x} 109v243q0 25 25 25h190q25 0 25-25V109M${x - 5} 108h250M${x - 5} 98h250`} /></g>)}
      <text x="200" y="420" textAnchor="middle" className="elevate-rocks-result">Critical work left out</text>
      <text x="600" y="420" textAnchor="middle" className="elevate-rocks-result">Small fixes fit around it</text>
    </svg>
    <div className="elevate-other-lenses">{slide.sections?.slice(1).map(item => <article key={item.title}><h2>{item.title}</h2><p>{item.body}</p></article>)}</div>
  </div>;
};

const Matrix = () => (
  <div className="elevate-matrix">
    <p className="elevate-axis-y">Impact increases ↑</p>
    <div className="elevate-matrix-cells">
      <div><h2>Quick candidate</h2><p>Useful benefit<br />Manageable effort</p></div>
      <div><h2>Stage the work</h2><p>High potential<br />Dependencies to resolve</p></div>
      <div><h2>Small improvement</h2><p>Limited benefit<br />Match investment to the benefit</p></div>
      <div><h2>Defer / investigate</h2><p>High effort<br />Unclear or limited return</p></div>
    </div>
    <p className="elevate-axis-x">Effort increases →</p>
    <p className="elevate-risk-gate">Before proceeding: check permissions, consequences, review, and recovery</p>
  </div>
);

const Tam = ({ description }: { description: string }) => {
  const marker = `tam-arrow-${useId().replace(/:/g, "")}`;
  const nodes = [
    { x: 10, y: 145, w: 145, label: ["External", "factors"] },
    { x: 205, y: 25, w: 190, label: ["Perceived", "usefulness"] },
    { x: 205, y: 255, w: 190, label: ["Perceived", "ease of use"] },
    { x: 460, y: 145, w: 145, label: ["Attitude", "toward use"] },
    { x: 675, y: 145, w: 145, label: ["Intention", "to use"] },
    { x: 880, y: 145, w: 145, label: ["Actual", "use"] },
  ];
  const paths = ["M155 175 L198 66", "M155 195 L198 292", "M300 250 V112", "M395 75 L453 166", "M395 295 L453 205", "M395 65 H747 V137", "M605 185 H667", "M820 185 H872"];
  return <><svg className="elevate-tam" viewBox="0 0 1040 380" role="img" aria-label={description}>
    <title>Technology Acceptance Model: beliefs, attitude, intention, and use</title>
    <defs><marker id={marker} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0 L10 5 L0 10 z" fill="currentColor" /></marker></defs>
    <g fill="none" stroke="currentColor" strokeWidth="3" markerEnd={`url(#${marker})`}>{paths.map(d => <path key={d} d={d} />)}</g>
    {nodes.map(n => <g key={n.label.join(" ")}><rect x={n.x} y={n.y} width={n.w} height="80" rx="2" className="elevate-tam-node" /><text x={n.x + n.w / 2} y={n.y + 33} textAnchor="middle">{n.label.map((line, i) => <tspan key={line} x={n.x + n.w / 2} dy={i === 0 ? 0 : 27}>{line}</tspan>)}</text></g>)}
    <text x="747" y="40" textAnchor="middle" className="elevate-tam-path-label">Usefulness also influences intention</text>
  </svg><div className="elevate-tam-mobile" role="img" aria-label={description}>
    <p>External factors</p><span aria-hidden="true">↓</span>
    <div><h2>Perceived usefulness</h2><h2>Perceived ease of use</h2><p>Ease can also influence usefulness.</p></div><span aria-hidden="true">↓</span>
    <p>Attitude toward use</p><span aria-hidden="true">↓</span>
    <div><h2>Intention to use</h2><p>Usefulness also influences intention directly.</p></div><span aria-hidden="true">↓</span><p>Actual use</p>
  </div></>;
};

const Quadrants = ({ slide }: Pick<Props, "slide">) => (
  <div className="elevate-levers">
    {slide.sections?.map((item, i) => <div key={item.title}><span aria-hidden="true">0{i + 1}</span><h2>{item.title}</h2><p>{item.body}</p></div>)}
    <strong className="elevate-levers-center">Shared outcome</strong>
  </div>
);

const Effort = () => (
  <div className="elevate-effort">
    <p className="elevate-diagram-label">Illustrative total effort · minutes</p>
    <div className="elevate-effort-row"><h2>Current process <strong>25 min</strong></h2><div className="elevate-effort-track"><span className="elevate-effort-prep" style={{ flex: 20 }}>20</span><span className="elevate-effort-review" style={{ flex: 5 }}>5</span><span className="elevate-effort-empty" style={{ flex: 2 }} /></div><p>20 preparation + 5 review</p></div>
    <div className="elevate-effort-row"><h2>Assisted process <strong>27 min</strong></h2><div className="elevate-effort-track"><span className="elevate-effort-prep" style={{ flex: 8 }}>8</span><span className="elevate-effort-review" style={{ flex: 12 }}>12</span><span className="elevate-effort-rework" style={{ flex: 7 }}>7</span></div><p>8 drafting + 12 review + 7 corrections</p></div>
    <div className="elevate-effort-result"><strong>+2 minutes</strong><p>Drafting is faster. Total effort is higher.<br />Check quality and rework before expanding the pilot.</p></div>
  </div>
);

const SourceImage = ({ visual }: { visual: VisualSpec }) => visual.viewBox && visual.imageSize
  ? <svg className="elevate-source-image elevate-cropped-image" viewBox={visual.viewBox} role="img" aria-label={visual.description}><title>{visual.description}</title><image href={visual.image} width={visual.imageSize[0]} height={visual.imageSize[1]} /></svg>
  : <img className="elevate-source-image" src={visual.image} alt={visual.description} decoding="async" />;

const Tree = ({ visual }: { visual: VisualSpec }) => (
  <div className="elevate-tree">
    {[{ box: "19 12 176 290", label: "The request", text: "How the customer explained it" }, { box: "400 12 176 290", label: "The interpretation", text: "How the analyst designed it" }, { box: "970 363 176 290", label: "The need", text: "What the customer really needed" }].map(panel => <figure key={panel.label}><svg viewBox={panel.box} role="img" aria-label={panel.text}><title>{panel.text}</title><image href={visual.image} width="1167" height="720" /></svg><figcaption><strong>{panel.label}</strong><span>{panel.text}</span></figcaption></figure>)}
  </div>
);

const TThinking = () => (
  <div className="elevate-t-thinking">
    <div className="elevate-t-horizontal"><span className="elevate-diagram-label">Horizontal · Shared direction</span><h2>Business priorities · Common standards · Shared learning</h2></div>
    <div className="elevate-t-vertical"><span className="elevate-diagram-label">Vertical · Local execution</span><h2>One workflow</h2><span aria-hidden="true">↓</span><h2>Named owners</h2><span aria-hidden="true">↓</span><h2>Practice & evidence</h2></div>
  </div>
);

const Pyramid = () => (
  <div className="elevate-pyramid">
    <div><span className="elevate-diagram-label">Recommendation</span><h2>Approve a bounded briefing pilot</h2></div>
    <div><span className="elevate-diagram-label">Supporting reasons</span><p>Recurring work · Visible gaps · Human review</p></div>
    <div><span className="elevate-diagram-label">Evidence & specific ask</span><p>Baseline + unresolved permissions<br />Named reviewer + protected test time</p></div>
  </div>
);

const Charter = () => (
  <div className="elevate-charter">
    <header><span className="elevate-diagram-label">Fictional pilot charter · Working draft</span><h2>Shipment-exception brief</h2><p>Draft only. A person approves every communication and operational action.</p></header>
    <dl>
      <div><dt>Purpose & scope</dt><dd>Make missing owners and conflicting dates visible. No sending or record changes.</dd></div>
      <div><dt>People & capacity</dt><dd>Name the sponsor, workflow owner, reviewer, support, and time protected for testing.</dd></div>
      <div><dt>Value & controls</dt><dd>Compare total effort and quality with the baseline. Confirm data permission. Pause if the approved limits are breached.</dd></div>
      <div><dt>Decision & review</dt><dd>Agree on the test period and quality standard. Bring the evidence to the decision owner on the review date.</dd></div>
    </dl>
  </div>
);

const Johari = ({ slide }: Pick<Props, "slide">) => (
  <div className="elevate-johari">
    <span /><p>I know</p><p>I don't know yet</p>
    <p>My reviewer<br />knows</p>
    {slide.sections?.slice(0, 2).map(item => <div key={item.title}><h2>{item.title}</h2><p>{item.body}</p></div>)}
    <p>My reviewer<br />doesn't know yet</p>
    {slide.sections?.slice(2).map(item => <div key={item.title}><h2>{item.title}</h2><p>{item.body}</p></div>)}
  </div>
);

const Hub = () => (
  <div className="elevate-hub">
    <div className="elevate-hub-center"><span className="elevate-diagram-label">Shared support</span><h2>AI Center of Excellence</h2><p>Help approve tools and data. Support testing.</p></div>
    <p className="elevate-hub-connection">Shared guidance ↓ &nbsp; ↑ Local evidence and feedback</p>
    <div className="elevate-hub-spokes">{[
      ["Operations lead", "Own the briefing workflow and the decision to pause it."],
      ["Reviewer", "Check each brief before use."],
      ["Briefing team", "Help people use it. Measure total effort and quality."],
    ].map(([title, body]) => <div key={title}><h2>{title}</h2><p>{body}</p></div>)}</div>
    <p className="elevate-diagram-footnote">Existing teams can provide this support without a formal Center of Excellence.</p>
  </div>
);

const Loop = () => (
  <div className="elevate-loop">
    {[
      ["Observe", "A required source is missing. Hold the briefing draft."],
      ["Assess", "The reviewer investigates. The operations lead can pause the workflow."],
      ["Respond", "Keep the evidence and correct the draft."],
      ["Review", "Check again before use. Retest workflow changes before restarting."],
    ].map(([title, body], i) => <div key={title}><span aria-hidden="true">0{i + 1}</span><h2>{title}</h2><p>{body}</p></div>)}
    <p className="elevate-loop-return">↶ Compare quality and total effort with the previous process. Continue, adjust, or stop.</p>
  </div>
);

const Chain = () => (
  <div className="elevate-chain">
    <div>{["Vision", "Consensus", "Skills", "Incentives", "Resources", "Action plan"].map((title, i) => <div key={title} data-gap={title === "Resources"}><span aria-hidden="true">{i + 1}</span><h2>{title}</h2>{title === "Resources" && <p>Time is missing</p>}</div>)}</div>
    <p className="elevate-chain-response"><strong>Ask what's missing</strong>The reviewer knows the method but has no time to practice. Protect practice time, then check whether the work improves.</p>
  </div>
);

const Ladder = ({ slide }: Pick<Props, "slide">) => (
  <ol className="elevate-ladder">{slide.sections?.map((item, i) => <li key={item.title}><span aria-hidden="true">0{i + 1}</span><div><h2>{item.title}</h2><p>{item.body}</p></div></li>)}</ol>
);

const Diagram = ({ slide, visual }: Props) => {
  switch (visual.kind) {
    case "image": return <SourceImage visual={visual} />;
    case "rules": return <Rules />;
    case "learning": return <Learning />;
    case "flow": return <Flow slide={slide} />;
    case "document": return <Brief slide={slide} />;
    case "matrix": return <Matrix />;
    case "tam": return <Tam description={visual.description} />;
    case "quadrants": return <Quadrants slide={slide} />;
    case "effort": return <Effort />;
    case "tree": return <Tree visual={visual} />;
    case "t-thinking": return <TThinking />;
    case "pyramid": return <Pyramid />;
    case "charter": return <Charter />;
    case "johari": return <Johari slide={slide} />;
    case "hub": return <Hub />;
    case "loop": return <Loop />;
    case "chain": return <Chain />;
    case "ladder": return <Ladder slide={slide} />;
    case "boundaries": return <Boundaries slide={slide} />;
    case "relationships": return <Relationships />;
    case "capability-gap": return <CapabilityGap />;
    case "agent-risks": return <AgentRisks />;
    case "governance-domains": return <GovernanceDomains slide={slide} />;
    case "big-rocks": return <BigRocks slide={slide} />;
    case "four-gaps": return <FourGaps slide={slide} />;
  }
};

export const ElevateLeadershipVisual = ({ slide, visual }: Props) => {
  const sourceUrl = visual.sourceUrl ?? slide.sourceUrl;
  const sourceLabel = visual.sourceLabel ?? slide.sourceLabel;
  return <div data-layout="elevate-visual" data-visual-kind={visual.kind} data-visual-slide={slide.id} className="elevate-visual">
    <header className="elevate-visual-header">
      <p className="elevate-diagram-label">Session {slide.session} · {slide.eyebrow?.includes("Optional reference") ? "Optional reference" : slide.eyebrow?.includes("Fictional") ? "Worked example" : "Visual explanation"}</p>
      <h1 className="s2p-editorial">{slide.title}</h1>
      <p className="elevate-visual-lesson">{visual.lesson}</p>
      <div className="elevate-visual-question"><span>Discuss</span><p>{visual.question}</p></div>
      {slide.warning && <p className="elevate-visual-warning">{slide.warning}</p>}
    </header>
    <figure className="elevate-visual-figure" aria-label={visual.description}>
      <div className="elevate-diagram"><Diagram slide={slide} visual={visual} /></div>
      <figcaption>
        {(visual.caption ?? slide.note) && <p>{visual.caption ?? slide.note}</p>}
        {(visual.credit || sourceUrl || visual.image) && <p className="elevate-visual-credit">{visual.credit}{sourceUrl && <>{visual.credit && " · "}<a href={sourceUrl} target="_blank" rel="noopener noreferrer">{sourceLabel}</a></>}{visual.image && <>{(visual.credit || sourceUrl) && " · "}<a href={visual.image} target="_blank" rel="noopener noreferrer">Open full original image</a></>}</p>}
      </figcaption>
    </figure>
  </div>;
};
